import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Hash, Star, MessageCircle, Calendar, ScrollText, BookOpen, ShieldCheck, Zap, Heart, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import VisitorCounter from "@/components/VisitorCounter";
import { articles } from "@/data/articles";

const features = [
  { icon: Sparkles, title: "Vedic Birth Chart", desc: "Precise sidereal chart with planets, nakshatras & houses" },
  { icon: Hash, title: "Lucky Numbers", desc: "Discover your lucky numbers, car, house, mobile numbers" },
  { icon: ScrollText, title: "Name Numerology", desc: "Pythagorean & Chaldean name vibration analysis" },
  { icon: Calendar, title: "Daily Panchang", desc: "Tithi, Ekadashi, Purnima, Amavasya, Nakshatra & Hora" },
  { icon: Star, title: "Daily Horoscope", desc: "AI-powered horoscope tuned to your zodiac" },
  { icon: MessageCircle, title: "AI Astro Talk", desc: "Chat with Cosmic Sage — your personal astrologer" },
];

const FAQS = [
  {
    q: "Do I need to create an account to use Cosmic Sage?",
    a: "No. Every calculator — numerology, birth chart, Panchang, Lo Shu grid, Kundli matching — works for guest users without an account. Signing in is optional and only unlocks saved charts and reading history across devices.",
  },
  {
    q: "Is Cosmic Sage really free?",
    a: "Yes, every feature is free. We fund the platform using short, user-initiated rewarded video ads. You start with 3 free premium readings (palm reading, AI chat, Vastu analysis). When you need more, you can watch a 5-second ad to unlock additional readings. No subscriptions, no paywalls, no credit cards.",
  },
  {
    q: "Which ayanamsa and ephemeris do you use?",
    a: "We use the Lahiri ayanamsa (the official Indian government standard) and Swiss Ephemeris for planetary positions. This is the same calculation standard used by professional Vedic astrologers and matches what you would get from a traditional panchang.",
  },
  {
    q: "How accurate are the AI-generated readings?",
    a: "Our AI readings are generated by Anthropic's Claude Sonnet with a Vedic-specific system prompt. Every reading is grounded in your actual natal chart data — not generic sun-sign paragraphs. The AI quotes specific planets, houses, Nakshatras and Dasha periods from your real chart. However, AI readings are a reflection aid; they are not a substitute for consultation with a qualified Jyotishi for major life decisions.",
  },
  {
    q: "What is the difference between Pythagorean and Chaldean numerology?",
    a: "Pythagorean (the modern western system) assigns A=1 through I=9 and restarts. Chaldean (the older Babylonian system) uses a sound-based mapping and reserves 9 for sacred use. Our calculator supports both. Chaldean is traditionally preferred by Indian numerologists; Pythagorean is faster and more forgiving. Try both and see which resonates.",
  },
  {
    q: "Does the Vastu Analyzer require a professional floor plan?",
    a: "No. Any clear image of your home layout works — a hand drawing, a builder's plan, a Google Maps screenshot of your plot. You then drag the compass to true north, pick the exact centre of the plot, and the AI reads all 16 directions against classical Vastu rules.",
  },
  {
    q: "Will my personal data or birth details be shared?",
    a: "No. We never sell personal data. Birth details are used only to compute your chart and (if you have an account) to save your readings. Palm photographs are analysed in-memory and discarded. Read our Privacy Policy for the full details.",
  },
  {
    q: "Can I use Cosmic Sage offline?",
    a: "The web app installs as a Progressive Web App (PWA) and caches the most-used tools for offline use. A native Android and iOS app with full offline support is also in the works.",
  },
];

const DEFAULT_TITLE = "Cosomic Sage — Vedic Astrology, Numerology & AI Astro Talk";
const DEFAULT_DESC = "Free Vedic astrology & numerology — birth chart, Vimshottari Dasha, Guna Milan matching, Lo Shu grid, daily Panchang, Vastu analyzer, palm reading & AI Astro Talk. In-depth guides included.";
const SITE_ORIGIN = "https://cosomicsage.com";

function setMeta(selector, attr, value) {
  const el = document.querySelector(selector);
  if (el) el.setAttribute(attr, value);
}

export default function Landing() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    // When SPA-navigating back to Landing from an Article/Learn page, reset
    // the per-page SEO meta tags so the homepage does not keep an article's
    // title and canonical.
    document.title = DEFAULT_TITLE;
    setMeta('meta[name="description"]', "content", DEFAULT_DESC);
    setMeta('link[rel="canonical"]', "href", `${SITE_ORIGIN}/`);
    setMeta('meta[property="og:url"]', "content", `${SITE_ORIGIN}/`);
    setMeta('meta[property="og:title"]', "content", "Cosomic Sage");
    setMeta('meta[property="og:description"]', "content", DEFAULT_DESC);
    setMeta('meta[property="og:type"]', "content", "website");
  }, []);

  useEffect(() => {
    api.get("/stats/users").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 starfield opacity-60" />
      <div className="absolute inset-0 hero-overlay" />

      <header className="relative z-10 max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-gold" />
          <span className="font-serif text-2xl text-slate-50">Cosomic Sage</span>
        </div>
        <div className="flex gap-2">
          <Link to="/learn">
            <Button variant="ghost" data-testid="landing-learn-btn" className="text-slate-200 hover:text-amber-300 hover:bg-white/5">
              Learn
            </Button>
          </Link>
          <Link to="/dashboard">
            <Button variant="ghost" data-testid="landing-explore-btn" className="text-slate-200 hover:text-amber-300 hover:bg-white/5">
              Explore
            </Button>
          </Link>
          <Link to="/login">
            <Button variant="ghost" data-testid="landing-login-btn" className="text-slate-200 hover:text-amber-300 hover:bg-white/5">
              Sign in
            </Button>
          </Link>
          <Link to="/dashboard">
            <Button data-testid="landing-register-btn" className="bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full font-medium px-5">
              Get Started
            </Button>
          </Link>
        </div>
      </header>

      <section className="relative z-10 max-w-5xl mx-auto px-6 pt-16 pb-24 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass border border-amber-500/20 mb-8 animate-fade-up">
          <Star className="w-3.5 h-3.5 text-gold" />
          <span className="text-xs font-medium text-amber-300 tracking-wider uppercase">
            Vedic + Numerology · Powered by AI
          </span>
        </div>

        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-slate-50 leading-[1.05] mb-6 animate-fade-up">
          Decode your <span className="text-gold italic">cosmic blueprint</span>
        </h1>
        <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-up">
          Accurate birth charts, numerology calculators, daily panchang & a wise AI astrologer —
          all in one beautifully crafted experience.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center animate-fade-up">
          <Link to="/dashboard">
            <Button data-testid="hero-start-btn" size="lg" className="bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full font-medium px-8 py-6 text-base gold-glow">
              Start Your Reading
            </Button>
          </Link>
          <Link to="/login">
            <Button data-testid="hero-signin-btn" size="lg" variant="outline" className="rounded-full border-white/20 text-slate-200 hover:bg-white/5 hover:text-amber-300 px-8 py-6 text-base">
              Sign in (optional)
            </Button>
          </Link>
        </div>

        {stats && stats.total_users > 0 && (
          <div
            data-testid="landing-user-count"
            className="mt-10 inline-flex items-center gap-2.5 px-4 py-2 rounded-full glass border border-amber-500/20 text-sm text-slate-300 animate-fade-up"
          >
            <div className="flex -space-x-2">
              <span className="w-6 h-6 rounded-full bg-amber-500/30 border border-amber-500/40 flex items-center justify-center text-[10px] text-amber-200">✦</span>
              <span className="w-6 h-6 rounded-full bg-rose-500/30 border border-rose-500/40 flex items-center justify-center text-[10px] text-rose-200">★</span>
              <span className="w-6 h-6 rounded-full bg-emerald-500/30 border border-emerald-500/40 flex items-center justify-center text-[10px] text-emerald-200">☾</span>
            </div>
            <span>
              <span className="text-amber-300 font-semibold" data-testid="landing-user-count-value">
                {stats.total_users.toLocaleString()}+
              </span>{" "}
              seekers have already decoded their cosmic blueprint
            </span>
          </div>
        )}
      </section>

      <section className="relative z-10 max-w-7xl mx-auto px-6 pb-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <div
              key={f.title}
              className="glass rounded-2xl p-6 card-hover"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <f.icon className="w-7 h-7 text-gold mb-4" />
              <h3 className="font-serif text-xl text-slate-50 mb-2">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why Cosmic Sage — content-heavy section for SEO/AdSense */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-16 border-t border-white/5">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-500/20 bg-amber-500/5 mb-5">
            <Star className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-xs tracking-wider uppercase text-amber-300">Why Cosmic Sage</span>
          </div>
          <h2 className="font-serif text-4xl text-slate-50 mb-6 leading-tight">
            Classical Jyotish, computed honestly — then explained in plain English.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-8">
          <div className="space-y-4 text-slate-300 leading-relaxed">
            <p>
              Most astrology apps shortcut the hard parts. They use tropical calculations labelled as
              Vedic, skip Vimshottari Dasha entirely, and lean on a few canned paragraphs for every
              reading. Cosmic Sage is built the other way around. Every birth chart on this site is
              computed with <b className="text-amber-200">Swiss Ephemeris</b> using the <b className="text-amber-200">Lahiri ayanamsa</b> — the same engine
              and sidereal system used by professional Jyotishis.
            </p>
            <p>
              Our numerology module supports both <b className="text-amber-200">Pythagorean and Chaldean</b> systems with
              independent calculations for Driver, Conductor, Soul Urge, Expression and Personality
              numbers. The Lo Shu grid is drawn from your full birth date including planes and missing
              numbers. Panchang shows all five limbs — Tithi, Vaar, Nakshatra, Yoga and Karana — for
              seven days at a glance.
            </p>
          </div>
          <div className="space-y-4 text-slate-300 leading-relaxed">
            <p>
              AI readings use <b className="text-amber-200">Anthropic's Claude Sonnet</b> with a Vedic-specific system prompt,
              and every reading is grounded in your actual chart data — not generic sun-sign
              paragraphs. The AI Astro Talk feature remembers your last five conversations so you
              can continue a reading where you left off.
            </p>
            <p>
              And everything is <b className="text-amber-200">free</b>. No subscription, no credit card, no upsell. We fund the
              platform with short, user-initiated rewarded ads — you watch a 5-second video, you get
              more readings. That's it. No account required to start.
            </p>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-5 rounded-2xl bg-slate-900/50 border border-white/5" data-testid="landing-why-swiss">
            <ShieldCheck className="w-5 h-5 text-amber-400 mb-3" />
            <div className="font-serif text-lg text-slate-50">Swiss Ephemeris accuracy</div>
            <p className="text-sm text-slate-400 mt-1">
              The same calculation library used by professional astrologers worldwide — accurate to
              a fraction of an arc-second.
            </p>
          </div>
          <div className="p-5 rounded-2xl bg-slate-900/50 border border-white/5" data-testid="landing-why-ai">
            <Zap className="w-5 h-5 text-amber-400 mb-3" />
            <div className="font-serif text-lg text-slate-50">AI grounded in your chart</div>
            <p className="text-sm text-slate-400 mt-1">
              Readings quote specific planets, houses and Dashas from your real natal chart — not
              generic horoscope text.
            </p>
          </div>
          <div className="p-5 rounded-2xl bg-slate-900/50 border border-white/5" data-testid="landing-why-free">
            <Heart className="w-5 h-5 text-amber-400 mb-3" />
            <div className="font-serif text-lg text-slate-50">Genuinely free</div>
            <p className="text-sm text-slate-400 mt-1">
              No subscriptions, no paywalls, no credit card. Supported by short optional ads so
              every feature stays free for everyone.
            </p>
          </div>
        </div>
      </section>

      {/* Learn section — article previews drive content depth */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-16 border-t border-white/5">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-500/20 bg-amber-500/5 mb-4">
              <BookOpen className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs tracking-wider uppercase text-amber-300">Guides</span>
            </div>
            <h2 className="font-serif text-4xl text-slate-50 leading-tight">
              Learn the tradition, properly
            </h2>
            <p className="text-slate-400 mt-3 max-w-xl">
              In-depth, original articles on Vedic astrology, numerology, Vastu and palmistry — written
              for curious beginners, not sold to seekers.
            </p>
          </div>
          <Link
            to="/learn"
            data-testid="landing-learn-all"
            className="text-amber-300 hover:text-amber-200 text-sm font-medium"
          >
            View all guides →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {articles.slice(0, 6).map((a) => (
            <Link
              key={a.slug}
              to={`/learn/${a.slug}`}
              data-testid={`landing-article-${a.slug}`}
              className="group p-5 rounded-2xl bg-slate-900/50 border border-white/5 hover:border-amber-500/30 transition-colors"
            >
              <span className="text-xs uppercase tracking-wider text-amber-300/80">{a.category}</span>
              <h3 className="font-serif text-lg text-slate-50 mt-2 group-hover:text-amber-200 leading-snug line-clamp-2">
                {a.title}
              </h3>
              <p className="text-xs text-slate-500 mt-2">{a.readMinutes} min read</p>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ section — classic AdSense-friendly content block */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-16 border-t border-white/5">
        <h2 className="font-serif text-4xl text-slate-50 text-center mb-10 leading-tight">
          Frequently asked questions
        </h2>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <details key={i} className="group p-5 rounded-2xl bg-slate-900/50 border border-white/5" data-testid={`landing-faq-${i}`}>
              <summary className="flex items-center justify-between cursor-pointer list-none">
                <span className="font-serif text-lg text-slate-100">{f.q}</span>
                <ChevronDown className="w-4 h-4 text-slate-400 transition-transform group-open:rotate-180" />
              </summary>
              <p className="mt-4 text-sm text-slate-400 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="relative z-10 max-w-7xl mx-auto px-6 pb-12 text-center space-y-4">
        <VisitorCounter />
        <div className="flex items-center justify-center gap-4 text-xs text-slate-500 flex-wrap">
          <Link to="/about" data-testid="landing-footer-about" className="hover:text-amber-300">About</Link>
          <span className="text-slate-700">·</span>
          <Link to="/learn" data-testid="landing-footer-learn" className="hover:text-amber-300">Guides</Link>
          <span className="text-slate-700">·</span>
          <Link to="/privacy" data-testid="landing-footer-privacy" className="hover:text-amber-300">Privacy Policy</Link>
          <span className="text-slate-700">·</span>
          <Link to="/terms" data-testid="landing-footer-terms" className="hover:text-amber-300">Terms of Service</Link>
          <span className="text-slate-700">·</span>
          <a href="mailto:support@cosomicsage.com" className="hover:text-amber-300">Contact</a>
        </div>
        <p className="text-amber-300/70 text-sm tracking-wider">
          Powered by <span className="font-medium text-amber-300">Gamya Consultancy and Electric Solution Pvt Ltd</span>
        </p>
      </section>
    </div>
  );
}
